import { inferRouteObject } from 'react-router-typed-object';
import { z } from 'zod';

import { InvoiceState, type IdAccountDto } from '@/services/ws-invoices/src/main/resources/rest-api';
import { Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles, not, or, and } from '@/js/libs/use-auth';

import RouteParamsValidator from 'src/components/RouteParamsValidator.tsx';
import { DEFAULT_PAYOUT_DATE } from 'src/scenes/Finances/constants.ts';
import { validatePayoutDate } from 'src/scenes/Finances/helpers.ts';
import { Finances } from 'src/scenes/Finances/index.ts';
import { PayoutConfigsSearchKey, PayoutReportsSearchKey, PayoutWebsitesSearchKey } from 'src/scenes/Finances/types.ts';
import PostFinances from 'src/scenes/PostFinances/index.ts';
import Status from 'src/scenes/Status/index.ts';

const index = true;

export const financesRouteObject = inferRouteObject({
  path: 'finances',
  element: (
    <PrivateRoute
      roles={and(
        or(Roles.ADMIN_INVOICE, Roles.PAYOUT, Roles.ADMIN, Roles.POSTADMANAGER, Roles.PAYMENTS, Roles.MANAGE_BOOKING_PERIODS),
        not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)),
      )}
      noAccessComponent={Status.NotFound}
    />
  ),
  children: [
    {
      Component: Finances.Helmet,
      children: [
        {
          element: <PrivateRoute roles={Roles.POSTADMANAGER} />,
          children: [
            {
              path: 'post-invoices',
              element: <PostFinances.Layout />,
              children: [
                {
                  path: ':state',
                  Component: PostFinances.Invoices,
                },
                {
                  index,
                  element: <Navigate to={`./${InvoiceState.OPEN}`} replace />,
                },
              ],
            },
          ],
        },
        {
          element: (
            <PrivateRoute
              roles={or(Roles.ADMIN_INVOICE, Roles.PAYOUT, Roles.ADMIN, Roles.POSTADMANAGER, Roles.PAYMENTS)}
              redirectPath='/finances/booking-periods'
            />
          ),
          children: [
            {
              path: 'invoices',
              element: <PrivateRoute roles={not(Roles.POSTADMANAGER)} />,
              children: [
                {
                  path: ':invoiceState',
                  Component: Finances.Invoices,
                  searchParams: z.object({
                    agencyId: z.custom<IdAccountDto>().optional(),
                    unpaidOnly: z.boolean().optional(),
                    search: z.string().optional(),
                  }).parse,
                },
                {
                  index,
                  element: <Navigate to={`./${InvoiceState.OPEN}`} replace />,
                },
              ],
            },
            {
              path: 'manual-invoice',
              children: [
                {
                  path: ':campaignId',
                  Component: Finances.ManualInvoice,
                },
                {
                  index,
                  element: <Navigate to={`../invoices/${InvoiceState.OPEN}`} replace />,
                },
              ],
            },
            { index, element: <Navigate to={`./invoices/${InvoiceState.OPEN}`} replace /> },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'company-configs',
              children: [
                { index, Component: Finances.CompanyConfigs.ConfigsList },
                { path: 'create', Component: Finances.CompanyConfigs.ConfigForm },
                { path: ':configId', Component: Finances.CompanyConfigs.ConfigForm },
              ],
            },
          ],
        },
        {
          path: 'payouts',
          element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'reports',
              children: [
                {
                  path: ':date',
                  element: <RouteParamsValidator validate={validatePayoutDate} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, element: <Finances.Payouts.Report /> }],
                  searchParams: z.object({
                    [PayoutReportsSearchKey.STATE_FILTER]: z.string().optional(),
                  }).parse,
                },
                {
                  index,
                  element: <Navigate to={`./${DEFAULT_PAYOUT_DATE}`} replace />,
                },
              ],
            },
            {
              path: 'statistics',
              children: [
                {
                  path: ':date',
                  element: <RouteParamsValidator validate={validatePayoutDate} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, element: <Finances.Payouts.Statistics /> }],
                },
                {
                  index,
                  element: <Navigate to={`./${DEFAULT_PAYOUT_DATE}`} replace />,
                },
              ],
            },
            {
              path: 'websites',
              searchParams: z.object({
                [PayoutWebsitesSearchKey.SEARCH]: z.string().optional(),
              }).parse,
              children: [
                {
                  path: ':id',
                  element: <Finances.Payouts.WebsiteForm />,
                },
                {
                  path: 'create',
                  element: <Finances.Payouts.WebsiteForm />,
                },
                {
                  path: 'create/:cloneId',
                  element: <Finances.Payouts.WebsiteForm />,
                },
                {
                  index,
                  element: <Finances.Payouts.Websites />,
                },
              ],
            },
            {
              path: 'payout-configs',
              searchParams: z.object({
                [PayoutConfigsSearchKey.SEARCH]: z.string().optional(),
              }).parse,
              children: [
                {
                  path: ':id',
                  element: <Finances.Payouts.PayoutConfigForm />,
                },
                {
                  path: 'create',
                  element: <Finances.Payouts.PayoutConfigForm />,
                },
                {
                  path: 'create/:cloneId',
                  element: <Finances.Payouts.PayoutConfigForm />,
                },
                {
                  index,
                  element: <Finances.Payouts.PayoutConfigs />,
                },
              ],
            },
            {
              path: 'positions',
              children: [
                {
                  path: ':date',
                  element: <RouteParamsValidator validate={validatePayoutDate} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, element: <Finances.Payouts.Positions /> }],
                },
                {
                  index,
                  element: <Navigate to={`./${DEFAULT_PAYOUT_DATE}`} replace />,
                },
              ],
            },
            {
              path: 'payout-id-ranges',
              children: [
                {
                  index,
                  element: <Finances.Payouts.CustomPayoutIdRanges />,
                },
              ],
            },
            {
              path: 'details',
              children: [
                {
                  path: ':date',
                  element: <RouteParamsValidator validate={validatePayoutDate} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, element: <Finances.Payouts.Details /> }],
                },
                {
                  index,
                  element: <Navigate to={`./${DEFAULT_PAYOUT_DATE}`} replace />,
                },
              ],
            },
            {
              index,
              element: <Navigate to={`./reports/${DEFAULT_PAYOUT_DATE}`} replace />,
            },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'credit-card-receipts',
              Component: Finances.CreditCardReceipts.Layout,
              children: [
                { index, element: <Navigate to='./payments' replace /> },
                { path: 'payments', Component: Finances.CreditCardReceipts.Payments },
                { path: 'payouts', Component: Finances.CreditCardReceipts.Payouts },
              ],
            },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.PAYMENTS} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'payments',
              Component: Finances.Payments,
            },
          ],
        },
      ],
    },
    {
      path: 'booking-periods',
      element: <PrivateRoute roles={Roles.MANAGE_BOOKING_PERIODS} noAccessComponent={Status.NotFound} />,
      children: [{ index, Component: Finances.BookingPeriods }],
    },
  ],
});
